import type { OrgMember } from '../../../services/organizationService';
import type { DeptUnit } from '../../../services/executiveDeptUnitsService';
import { resolveDeptManager } from '../../../services/executiveDeptUnitsService';
import type { TeamMemberWorkload, UnifiedWorkItem } from '../../../modules/minha-mesa/types/workItem';
import { CAPACITY_PER_OPERATOR } from './teamCapacityShared';

export const ORG_MEMBERS_FULL_QUERY_KEY = (organizationId: string) =>
  ['org-members-full', organizationId] as const;

export const TEAM_CAPACITY_WORK_ITEMS_KEY = (organizationId: string) =>
  ['team-capacity', organizationId, 'work-items'] as const;

export interface DeptUnitAggregate {
  teamCount: number;
  managerName: string | null;
  manager: OrgMember | null;
  projectCount: number;
  /** Carga FTE agregada (soma das frações de ocupação por operador). */
  fteLoad: number;
  actionCount: number;
  overdueCount: number;
  memberUserIds: string[];
  members: OrgMember[];
}

/** Membros do setor: org_members com department_id + fallback da unidade estática. */
export function resolveDeptMemberIds(
  deptId: string,
  unit: DeptUnit,
  orgMembers: OrgMember[],
): string[] {
  const ids = new Set<string>();
  for (const m of orgMembers) {
    if (m.department_id === deptId) ids.add(m.user_id);
  }
  for (const m of unit.members) {
    if (!m.department_id || m.department_id === deptId) ids.add(m.user_id);
  }
  return [...ids];
}

export function resolveDeptOrgMembers(
  deptId: string,
  unit: DeptUnit,
  orgMembers: OrgMember[],
): OrgMember[] {
  const byId = new Map<string, OrgMember>();
  for (const m of orgMembers) {
    if (m.department_id === deptId) byId.set(m.user_id, m);
  }
  for (const m of unit.members) {
    if ((!m.department_id || m.department_id === deptId) && !byId.has(m.user_id)) {
      byId.set(m.user_id, m);
    }
  }
  return [...byId.values()];
}

export function aggregateDeptUnit(
  deptId: string,
  unit: DeptUnit,
  orgMembers: OrgMember[],
  workloadMembers: TeamMemberWorkload[],
  items: UnifiedWorkItem[],
): DeptUnitAggregate {
  const members = resolveDeptOrgMembers(deptId, unit, orgMembers);
  const memberUserIds = members.map((m) => m.user_id);
  const idSet = new Set(memberUserIds);
  const workloadByUser = new Map(workloadMembers.map((m) => [m.user_id, m]));

  const manager = resolveDeptManager(members) ?? unit.manager;

  const deptItems = items.filter((i) => idSet.has(i.assigned_to));
  const activeItems = deptItems.filter((i) => i.status !== 'completed');

  const projectIds = new Set<string>();
  for (const p of unit.projects) projectIds.add(p.id);
  for (const i of deptItems) {
    if (i.project_id) projectIds.add(i.project_id);
  }

  const activeByUser = new Map<string, number>();
  for (const item of items) {
    if (!item.assigned_to || item.status === 'completed') continue;
    activeByUser.set(item.assigned_to, (activeByUser.get(item.assigned_to) ?? 0) + 1);
  }

  let fteLoad = 0;
  for (const uid of memberUserIds) {
    const active = activeByUser.get(uid) ?? workloadByUser.get(uid)?.total_active ?? 0;
    fteLoad += active / CAPACITY_PER_OPERATOR;
  }

  return {
    teamCount: memberUserIds.length,
    managerName: manager?.user_name ?? null,
    manager,
    projectCount: projectIds.size,
    fteLoad: Math.round(fteLoad * 10) / 10,
    actionCount: activeItems.length,
    overdueCount: activeItems.filter((i) => i.is_overdue).length,
    memberUserIds,
    members,
  };
}

export function buildAggregatesMap(
  units: DeptUnit[],
  orgMembers: OrgMember[],
  workloadMembers: TeamMemberWorkload[],
  items: UnifiedWorkItem[],
): Map<string, DeptUnitAggregate> {
  const map = new Map<string, DeptUnitAggregate>();
  for (const unit of units) {
    map.set(
      unit.department.id,
      aggregateDeptUnit(unit.department.id, unit, orgMembers, workloadMembers, items),
    );
  }
  return map;
}
